import { useEffect, useRef } from 'react';

interface FinalBlockProps {
  onApplyClick: () => void;
}

export default function FinalBlock({ onApplyClick }: FinalBlockProps) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="final" className="relative overflow-hidden" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 30% 40%, oklch(var(--secondary-500) / 0.4) 0%, transparent 50%), radial-gradient(circle at 70% 60%, oklch(var(--accent-500) / 0.2) 0%, transparent 40%)',
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left Column - Image */}
          <div className="reveal-on-scroll relative order-2 lg:order-1">
            <div className="overflow-hidden rounded-lg" style={{ aspectRatio: '4/5' }}>
              <img
                src="https://static.readdy.ai/image/e52027607460cffc32b49812e98784ba/bcd5c0890245367904df7f73a5b32776.jpeg"
                alt="Noiva sorrindo com confiança em ensaio editorial atemporal"
                title="The Future Wedding - Ribeirão Preto"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative element */}
            <div
              className="absolute -bottom-6 -left-6 w-32 h-32 rounded-lg -z-10 hidden lg:block"
              style={{ backgroundColor: 'oklch(var(--accent-500) / 0.12)' }}
            ></div>
          </div>

          {/* Right Column - Final Message */}
          <div className="reveal-on-scroll order-1 lg:order-2">
            <h2
              className="font-heading text-3xl md:text-5xl lg:text-6xl leading-[1.12] mb-8"
              style={{ color: 'oklch(var(--foreground-900))' }}
            >
              Algumas experiências
              <br />
              não se explicam.
              <br />
              Se vivem.
            </h2>

            <p className="text-sm md:text-base leading-relaxed mb-10 max-w-md" style={{ color: 'oklch(var(--foreground-600))' }}>
              As vagas são limitadas para garantir uma experiência intimista e
              cuidadosamente curada. Cada detalhe foi pensado para quem está pronta
              para viver essa transformação.
            </p>

            <button
              onClick={onApplyClick}
              className="group px-10 py-4 text-sm tracking-[0.2em] uppercase rounded-md transition-all duration-500 whitespace-nowrap cursor-pointer font-semibold relative overflow-hidden"
              style={{
                backgroundColor: '#ADC178',
                color: '#FFFFFF',
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                Garantir Minha Vaga
                <i className="ri-arrow-right-line text-lg"></i>
              </span>
              <div
                className="absolute inset-0 transition-transform duration-500 -translate-x-full group-hover:translate-x-0"
                style={{ backgroundColor: '#9AAF65' }}
              ></div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}